import { useCallback, useMemo, useRef } from 'react';
import { type UseFormReturn } from 'react-hook-form';
import {
  type FormHandlerRef,
  type FormHandlerState,
} from './FormCoordinatorTypes.tsx';

export type IUseFormCoordinator = ReturnType<typeof useFormCoordinator>;

/**
 * Punto centrale per la gestione del coordinamento dei form.
 *
 * Registra l'insieme dei form e per ognuno gestisce:
 *  - `methodsRefsMap`: ref ai metodi RHF di ogni form, per accesso diretto
 *    da componenti esterni (es. `useCoordinatedFormState`)
 *  - `formsStateRef`: stato aggregato (`isDirty`, `isValid`) di ogni form,
 *    mantenuto in un ref per evitare che ogni aggiornamento causi
 *    `propagateContextChange_eager` (~40ms con 200+ widget)
 *
 * I consumer reattivi (es. `FormPreventNavigation`, `FormCoordinatorBadge`)
 * si sottoscrivono tramite `subscribeToForms` / `getFormsSnapshot`
 * (pattern `useSyncExternalStore`).
 */
export function useFormCoordinator() {
  /**
   * Ref ai metodi RHF di ogni form registrato.
   * Usato da `useCoordinatedFormState` per accedere al `control` di un
   * form specifico e sottoscriversi direttamente ai suoi subject RHF.
   */
  const methodsRefsMap = useRef(new Map<string, FormHandlerRef>());

  /**
   * Stato aggregato dei form (isDirty, isValid).
   * Mantenuto in un ref (non useState) per evitare che ogni aggiornamento
   * cambi il context value → `propagateContextChange_eager`.
   *
   * Funge anche da snapshot per `useSyncExternalStore`: aggiornato SOLO
   * quando `isDirty` o `isValid` cambiano davvero. `getSnapshot()` ritorna
   * sempre questo ref, e `useSyncExternalStore` confronta per identità
   * referenziale — se punta allo stesso oggetto, non ri-renderizza.
   */
  const formsStateRef = useRef<Record<string, FormHandlerState>>({});
  const listeners = useRef(new Set<() => void>());

  const subscribeToForms = useCallback((listener: () => void) => {
    listeners.current.add(listener);
    return () => listeners.current.delete(listener);
  }, []);

  const getFormsSnapshot = useCallback(() => formsStateRef.current, []);

  const notifyListeners = useCallback(() => {
    for (const listener of listeners.current) listener();
  }, []);

  const registerForm = useCallback(
    (ref: React.MutableRefObject<UseFormReturn>, form: FormHandlerState) => {
      methodsRefsMap.current.set(form.id, { id: form.id, ref });
      const prev = formsStateRef.current[form.id];
      const changed =
        !prev || prev.isDirty !== form.isDirty || prev.isValid !== form.isValid;
      if (!changed) return;
      formsStateRef.current = { ...formsStateRef.current, [form.id]: form };
      notifyListeners();
    },
    [notifyListeners],
  );

  const unregisterForm = useCallback(
    (id: string) => {
      methodsRefsMap.current.delete(id);
      const { [id]: _, ...rest } = formsStateRef.current;
      formsStateRef.current = rest;
      notifyListeners();
    },
    [notifyListeners],
  );

  const watchFormValues = useCallback((formId: string | null | undefined) => {
    if (!formId) return null;
    const form = methodsRefsMap.current.get(formId);
    if (!form) return null;
    return form.ref.current.watch;
  }, []);

  return useMemo(
    () => ({
      formRefs: methodsRefsMap,
      registerForm,
      unregisterForm,
      watchFormValues,
      subscribeToForms,
      getFormsSnapshot,
    }),
    [
      registerForm,
      unregisterForm,
      watchFormValues,
      subscribeToForms,
      getFormsSnapshot,
    ],
  );
}
